/**
 * Promo Countdown - announcement bar sale countdown with ferrofluid reveal.
 *
 * Self-contained (does NOT reuse coretex-countdown, which is shared by
 * ls-countdown and only renders a bare end heading). Counts down to a
 * sale start instant, then at T=0 swaps the timer for the "live" message
 * and materializes it out of ferrofluid blobs - the same threshold-filter
 * look as c-morphing-text.js, but a one-shot reveal instead of a loop.
 *
 * Server-anchored: data-server-now carries the shop time (ISO with offset);
 * we compute clock skew so the timer is correct even on a wrong client clock.
 * Target wall-clock (data-target = "YYYY-MM-DDTHH:MM:SS") is resolved against
 * data-tz (default Europe/Warsaw) using Intl, so DST is handled correctly.
 *
 * Ticks every second, pauses when the tab is hidden. If the target is already
 * past on first paint, renders the live state immediately with no animation.
 *
 * Markup (rendered by sections/header-promo.liquid):
 *   <promo-countdown data-server-now data-target data-tz data-units data-ferrofluid>
 *     <span class="promo-cd__pre">SUMMER SALE STARTS IN</span>
 *     <span class="promo-cd__timer timer" aria-live="off"></span>
 *     <span class="promo-cd__live" hidden><a href="...">SUMMER SALE IS LIVE NOW</a></span>
 *   </promo-countdown>
 */

const TICK_MS = 1000;
const FILTER_ID = 'enrage-mt-threshold'; // shared with c-morphing-text.js
const FILTER_THRESHOLD = 60;

/* One global SVG alpha-cutoff filter; reused if c-morphing-text already made it. */
function ensureFilter() {
    if (document.getElementById(FILTER_ID)) return;
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
    const defs = document.createElementNS(NS, 'defs');
    const filter = document.createElementNS(NS, 'filter');
    filter.setAttribute('id', FILTER_ID);
    const cm = document.createElementNS(NS, 'feColorMatrix');
    cm.setAttribute('in', 'SourceGraphic');
    cm.setAttribute('type', 'matrix');
    cm.setAttribute('values', '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 255 ' + (-FILTER_THRESHOLD));
    filter.appendChild(cm);
    defs.appendChild(filter);
    svg.appendChild(defs);
    (document.body || document.documentElement).appendChild(svg);
}

/* Offset string like "+02:00" for the given instant in the given tz. */
function tzOffsetString(date, tz) {
    const longOffset = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        timeZoneName: 'longOffset',
    }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value || 'GMT+00:00';
    return longOffset.replace('GMT', '') || '+00:00';
}

/* Resolve "YYYY-MM-DDTHH:MM:SS" wall-clock in tz to a real instant (ms).
   Two-pass so the offset is taken at the target date itself (DST-correct). */
function resolveTarget(wall, tz) {
    if (!wall) return NaN;
    const naive = new Date(wall.replace(/-/g, '/').replace('T', ' ')).getTime();
    if (isNaN(naive)) return NaN;
    const off1 = tzOffsetString(new Date(naive), tz);
    const pass1 = new Date(wall + off1).getTime();
    const off2 = tzOffsetString(new Date(pass1), tz);
    return new Date(wall + off2).getTime();
}

class PromoCountdown extends HTMLElement {
    connectedCallback() {
        if (this._init) return;
        this._init = true;

        const serverNow = this.dataset.serverNow ? new Date(this.dataset.serverNow).getTime() : Date.now();
        this.skew = isNaN(serverNow) ? 0 : serverNow - Date.now();

        this.tz = this.dataset.tz || 'Europe/Warsaw';
        this.target = resolveTarget(this.dataset.target, this.tz);
        this.showDays = this.dataset.days !== 'false';
        this.showSeconds = this.dataset.seconds !== 'false';
        this.ferrofluid = this.dataset.ferrofluid === 'true';

        try {
            this.units = JSON.parse(this.dataset.units || '{}');
        } catch (e) {
            this.units = {};
        }
        this.units = Object.assign({ d: 'd', h: 'h', m: 'm', s: 's' }, this.units);

        this.preEl = this.querySelector('.promo-cd__pre');
        this.timerEl = this.querySelector('.promo-cd__timer');
        this.liveEl = this.querySelector('.promo-cd__live');

        if (isNaN(this.target)) return; // no/invalid date: leave teaser markup as-is

        this._tick = this._tick.bind(this);
        this._onVis = this._onVis.bind(this);

        if (this._now() >= this.target) {
            this._reveal(false); // already live on first paint, no animation
            return;
        }

        this._tick();
        this.interval = setInterval(this._tick, TICK_MS);
        document.addEventListener('visibilitychange', this._onVis);
    }

    disconnectedCallback() {
        clearInterval(this.interval);
        document.removeEventListener('visibilitychange', this._onVis);
    }

    _onVis() {
        if (document.hidden) {
            clearInterval(this.interval);
        } else {
            this._tick();
            this.interval = setInterval(this._tick, TICK_MS);
        }
    }

    _now() {
        return Date.now() + this.skew;
    }

    _tick() {
        const diff = this.target - this._now();
        if (diff <= 0) {
            clearInterval(this.interval);
            this._reveal(this.ferrofluid);
            return;
        }
        if (!this.timerEl) return;

        let rest = Math.floor(diff / 1000);
        const days = Math.floor(rest / 86400);
        // Only carve days into their own cell when we actually show a day cell.
        // When days are hidden, fold them into the hours so the timer reads as
        // total hours (e.g. 47h) instead of hours-mod-24 (e.g. 23h).
        const showDayCell = this.showDays && days > 0;
        if (showDayCell) rest -= days * 86400;
        const hours = Math.floor(rest / 3600); rest -= hours * 3600;
        const mins = Math.floor(rest / 60);
        const secs = rest - mins * 60;

        const u = this.units;
        const pad = n => String(n).padStart(2, '0');
        const cells = [];
        if (showDayCell) cells.push(this._cell(days, u.d));
        cells.push(this._cell(pad(hours), u.h));
        cells.push(this._cell(pad(mins), u.m));
        if (this.showSeconds) cells.push(this._cell(pad(secs), u.s));
        this.timerEl.innerHTML = cells.join('<span class="promo-cd__sep" aria-hidden="true">:</span>');
    }

    _cell(value, unit) {
        return `<span class="promo-cd__cell"><span class="promo-cd__num">${value}</span><span class="promo-cd__unit">${unit}</span></span>`;
    }

    _reveal(animate) {
        if (this.preEl) this.preEl.hidden = true;
        if (this.timerEl) this.timerEl.hidden = true;
        if (!this.liveEl) return;
        this.liveEl.hidden = false;
        this.dataset.state = 'live';

        if (!animate) return;
        const target = this.liveEl.querySelector('.promo-cd__livetext') || this.liveEl.firstElementChild || this.liveEl;
        ensureFilter();

        /* One-shot blob materialization: heavy blur under the alpha-cutoff
           filter collapses into solid letters as blur -> 0. */
        const DURATION = 900;
        const MAX_BLUR = 10;
        target.style.setProperty('filter', `url(#${FILTER_ID}) blur(${MAX_BLUR}px)`, 'important');
        target.style.opacity = '0';

        let start = null;
        const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const step = ts => {
            if (start === null) start = ts;
            const t = Math.min(1, (ts - start) / DURATION);
            const e = ease(t);
            const blur = MAX_BLUR * (1 - e);
            target.style.setProperty('filter', `url(#${FILTER_ID}) blur(${blur}px)`, 'important');
            target.style.opacity = String(e);
            if (t < 1) {
                requestAnimationFrame(step);
            } else {
                target.style.removeProperty('filter');
                target.style.opacity = '1';
            }
        };
        requestAnimationFrame(step);
    }
}

if (!customElements.get('promo-countdown')) {
    customElements.define('promo-countdown', PromoCountdown);
}
